/**
 * The table shell for positions, open orders, fills, history and settled.
 *
 * `row-height` 28px, `caption` header in `text-secondary` on `bg-surface`, 1px
 * `border-subtle` between rows, hover `bg-hover`, numeric columns right-aligned
 * and `.ud-num`, sticky header (components.md §3).
 *
 * Two of the four rules the 1-minute cadence forces are enforceable here and are:
 *
 * **Rows only enter at the top and leave from the bottom.** A new row appears
 * every 60 seconds, and if a sort key could change under a pointer the user
 * cancels the wrong order. The component takes rows in final order and never
 * sorts; `rowKey` is required so React keeps identity across an insertion.
 *
 * **Entry and exit animate on `duration-fast`, height only.** Sixty row
 * insertions an hour with a spring is motion sickness.
 *
 * The other two — sort by anchor descending, and pagination on Settled — belong
 * to the callers in Phase 6 and Phase 8, because only they know the key.
 *
 * Density: `dense` is the 22px orderbook row, `base` the 28px table row,
 * `comfortable` the 36px used below 768px and by positions.
 *
 * **Below 768px every row is a card**, and it is the same DOM. Squeezed into a
 * phone's width the numeric columns kept theirs and the text ones were
 * truncated to nothing — `btc-u…`, `Outco…`, `O…` — and every extra digit of a
 * bigger trade made it worse. So a row wraps instead: the columns a caller
 * marks `card: 'title'` form an unlabelled top line with any `badge` at its
 * right end, every other column becomes a `label value` pair two to a line,
 * and an `action` gets a line of its own. The label is the column's header,
 * drawn by `::before` from `data-label`, so a cell's text is still only its
 * value — for a screen reader, which also keeps the header row, and for every
 * test that reads one.
 */

import type { ReactNode } from 'react'
import { cx } from '@/lib/cx'

export type TableDensity = 'dense' | 'base' | 'comfortable'

const ROW_HEIGHT: Record<TableDensity, string> = {
  dense: 'h-[var(--ud-row-height-dense)]',
  base: 'h-[var(--ud-row-height)]',
  comfortable: 'h-[var(--ud-row-height-comfortable)]',
}

/** Where a column goes in the card a row becomes below 768px. */
export type TableCardSlot = 'title' | 'badge' | 'action'

export interface TableColumn<Row> {
  id: string
  header: ReactNode
  cell: (row: Row) => ReactNode
  numeric?: boolean
  /** Any CSS grid track size. Numeric columns are usually `max-content`. */
  width?: string
  /** Dropped below 1120px — the `market_id` line moves into the row expand. */
  hideBelowLg?: boolean
  /**
   * Its place in the phone card: `title` the unlabelled top line (what the
   * row *is* — market, outcome, side), `badge` that line's right end (a state
   * pill), `action` a line of its own (Cancel). Unset, it is a labelled pair.
   */
  card?: TableCardSlot
}

/** A row below 768px: a wrapping card rather than a band of tracks. */
const CARD_ROW =
  'max-md:flex max-md:h-auto max-md:flex-wrap max-md:items-center max-md:gap-x-4 max-md:gap-y-1 max-md:px-3 max-md:py-2'

/** Each slot's place in that card. `order` rather than DOM position, so the
 *  columns stay in the order the table has them from 768px up. */
const CARD_CELL: Record<TableCardSlot | 'pair', string> = {
  title: 'max-md:order-1 max-md:min-w-0 max-md:shrink',
  badge: 'max-md:order-2 max-md:ml-auto max-md:shrink-0',
  // Two to a line, never truncated: the whole point is that nothing is cut.
  pair: 'max-md:order-4 max-md:grow max-md:basis-[40%] max-md:overflow-visible max-md:whitespace-normal max-md:text-right',
  action: 'max-md:order-5 max-md:basis-full max-md:text-right',
}

/** A pair's label: its column header, from `data-label`, floated left of the
 *  right-aligned value. */
const CARD_LABEL =
  'max-md:before:float-left max-md:before:mr-2 max-md:before:font-sans max-md:before:text-micro max-md:before:text-text-tertiary max-md:before:content-[attr(data-label)]'

export interface TableProps<Row> {
  columns: readonly TableColumn<Row>[]
  rows: readonly Row[]
  rowKey: (row: Row) => string
  density?: TableDensity
  /** Rendered instead of rows. A single `body-sm` line, one ghost action. */
  empty?: ReactNode
  /** Skeleton rows while the snapshot is in flight — never a centred spinner. */
  loadingRows?: number
  onRowClick?: (row: Row) => void
  /**
   * Per-row styling, applied to the row rather than to each cell.
   *
   * Phase 6's optimistic order renders "at 60 % opacity with a `PENDING` pill"
   * (components.md §4.5), and that is a property of the *row*: dimming it cell
   * by cell is a rule every future column has to remember, and one that forgets
   * is a half-dimmed row.
   */
  rowClassName?: (row: Row) => string | undefined
  /** Data attributes for a row's state, so a browser test asserts on the DOM
   *  rather than on a computed opacity. */
  rowAttributes?: (row: Row) => Record<string, string>
  className?: string
  label: string
}

export function Table<Row>({
  columns,
  rows,
  rowKey,
  density = 'base',
  empty,
  loadingRows,
  onRowClick,
  rowClassName,
  rowAttributes,
  className,
  label,
}: TableProps<Row>) {
  const template = columns
    .map((c) => c.width ?? (c.numeric ? 'max-content' : 'minmax(0, 1fr)'))
    .join(' ')
  const showSkeleton = loadingRows !== undefined && loadingRows > 0 && rows.length === 0
  /** Whether a card has a top line, which is what the pairs wrap beneath. */
  const cardHead = columns.some((c) => c.card === 'title' || c.card === 'badge')

  /** Header, skeleton and data rows are all the same shape: a full-width band
   *  that borrows the table's own tracks. */
  const band = 'col-span-full grid grid-cols-subgrid items-center gap-3 px-2'

  return (
    // **One grid for the whole table, and the rows take its tracks by
    // `subgrid`.** Every row used to be its own grid carrying its own copy of
    // `gridTemplateColumns`, and `max-content` resolves against the content of
    // the grid it is in — so a row holding `$104.00` sized that track wider than
    // one holding `$0.00`, the header sized it to the word `Notional`, and no
    // two of the three lined up. The columns were only ever aligned by
    // coincidence, on the rows whose numbers happened to be the same width.
    //
    // `subgrid` is what makes a row a real box — hover, border, the click
    // target — while its columns belong to the table. `display: contents` would
    // align them too and would take all three of those away.
    <div
      role="table"
      aria-label={label}
      className={cx('ud-scroll grid min-h-0 content-start max-md:flex max-md:flex-col', className)}
      style={{ gridTemplateColumns: template }}
    >
      <div
        role="row"
        className={cx(
          band,
          'sticky top-0 z-10 border-b border-border-subtle bg-bg-surface',
          // Below 768px each card labels its own values; the header stays in
          // the DOM for a screen reader and takes no room.
          'max-md:sr-only',
          'text-caption text-text-secondary',
          ROW_HEIGHT[density],
        )}
      >
        {columns.map((c) => (
          <div
            key={c.id}
            role="columnheader"
            className={cx('truncate', c.numeric && 'text-right', c.hideBelowLg && 'hidden lg:block')}
          >
            {c.header}
          </div>
        ))}
      </div>

      {showSkeleton
        ? Array.from({ length: loadingRows }, (_, i) => (
            <div
              key={`skeleton-${i}`}
              className={cx(
                band,
                'border-b border-border-subtle',
                ROW_HEIGHT[density],
                'max-md:flex max-md:h-auto max-md:gap-3 max-md:px-3 max-md:py-3',
              )}
            >
              {columns.map((c) => (
                <div
                  key={c.id}
                  className={cx(
                    'ud-skeleton h-[calc(10*var(--ud-u))] rounded-xs',
                    c.numeric ? 'ml-auto w-12' : 'w-2/3',
                    c.hideBelowLg && 'hidden lg:block',
                  )}
                />
              ))}
            </div>
          ))
        : null}

      {!showSkeleton && rows.length === 0 && empty ? (
        <div className="col-span-full flex items-center justify-center py-6 text-body-sm text-text-tertiary">
          {empty}
        </div>
      ) : null}

      {rows.map((row) => (
        <div
          key={rowKey(row)}
          role="row"
          onClick={onRowClick ? () => onRowClick(row) : undefined}
          {...(rowAttributes ? rowAttributes(row) : {})}
          className={cx(
            band,
            'group border-b border-border-subtle',
            'transition-[height] duration-[var(--ud-duration-fast)] ease-out',
            ROW_HEIGHT[density],
            onRowClick && 'cursor-pointer',
            'hover:bg-bg-hover',
            CARD_ROW,
            rowClassName?.(row),
          )}
        >
          {columns.map((c) => {
            const label = !c.card && typeof c.header === 'string' && c.header !== '' ? c.header : undefined
            return (
              <div
                key={c.id}
                role="cell"
                data-label={label}
                className={cx(
                  'truncate text-body-sm text-text-primary',
                  c.numeric && 'ud-num text-right',
                  c.hideBelowLg && 'hidden lg:block',
                  CARD_CELL[c.card ?? 'pair'],
                  label && CARD_LABEL,
                )}
              >
                {c.cell(row)}
              </div>
            )
          })}
          {/* The line break between a card's top line and its pairs. Not a
              grid item from 768px up — `md:hidden` takes it out of the row's
              tracks entirely. */}
          {cardHead ? <div aria-hidden="true" className="order-3 h-0 basis-full md:hidden" /> : null}
        </div>
      ))}
    </div>
  )
}
